import React, { useEffect, useMemo, useState } from "react";
import { IoImageOutline } from "react-icons/io5";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { updateProductQuantity } from "../../redux/thunk";
import { resetUpdateQuantityStatus } from "../../redux/inventory/inventorySlice";
import type { InventoryProduct, PurchaseHistory } from "../../types/inventory";
import toast from "react-hot-toast";

function stockStatusFromQuantity(
  stock: number
): InventoryProduct["stock_status"] {
  if (stock <= 0) return "Red";
  if (stock <= 10) return "Yellow";
  return "Green";
}

/** Match `overviewProductExtras` on Sales Inventory so modal shows the same image as the table. */
function getProductImageUrl(
  product: InventoryProduct & Record<string, unknown>
): string {
  const str = (k: string) =>
    typeof product[k] === "string" ? (product[k] as string).trim() : "";
  const fromFields =
    str("product_image") ||
    str("productImage") ||
    str("image") ||
    str("imageUrl") ||
    str("thumbnail") ||
    str("photo");
  if (fromFields) return fromFields;
  const images = product.images;
  if (Array.isArray(images) && images.length > 0) {
    const first = images[0];
    if (typeof first === "string" && first.trim()) return first.trim();
  }
  return "";
}

function getStockStatusLabel(status: string): string {
  switch (status) {
    case "Red":
      return "Out of Stock";
    case "Yellow":
      return "Low Level";
    case "Green":
      return "Healthy Level";
    default:
      return status;
  }
}

function getStockStatusPillClass(status: string): string {
  switch (status) {
    case "Red":
      return "text-red-700 bg-red-50";
    case "Yellow":
      return "text-amber-700 bg-amber-50";
    case "Green":
      return "text-green-700 bg-green-50";
    default:
      return "text-gray-700 bg-gray-50";
  }
}

export interface PurchaseEntryModalProps {
  open: boolean;
  onClose: () => void;
  product: InventoryProduct;
  onSuccess: (
    updated: InventoryProduct,
    quantityPurchased?: number
  ) => void;
}

const PurchaseEntryModal: React.FC<PurchaseEntryModalProps> = ({
  open,
  onClose,
  product,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { firstName, lastName, adminLevel } = useAppSelector(
    (state) => state.auth
  );
  const updateQuantity = useAppSelector(
    (state) => state.inventory.updateQuantity
  );

  const [quantityInput, setQuantityInput] = useState("");
  const [productImageFailed, setProductImageFailed] = useState(false);

  const procurementOfficerDisplay = useMemo(() => {
    const name = [firstName, lastName].filter(Boolean).join(" ").trim();
    const role = adminLevel || "Admin";
    return name ? `${name} (${role})` : `Administrator (${role})`;
  }, [firstName, lastName, adminLevel]);

  const productImageUrl = useMemo(
    () => getProductImageUrl(product as InventoryProduct & Record<string, unknown>),
    [product]
  );

  useEffect(() => {
    if (open) {
      setQuantityInput("");
      setProductImageFailed(false);
      dispatch(resetUpdateQuantityStatus());
    }
  }, [open, product.product_id, dispatch]);

  if (!open) return null;

  const handleSubmit = async () => {
    const qty = parseInt(quantityInput.trim(), 10);
    if (!quantityInput.trim() || Number.isNaN(qty) || qty < 1) {
      toast.error("Enter a valid quantity (1 or more).");
      return;
    }
    try {
      const result = await dispatch(
        updateProductQuantity({
          productId: product.product_id,
          newQuantity: qty,
        })
      ).unwrap();

      const nextStock =
        typeof result?.product?.quantity === "number"
          ? result.product.quantity
          : product.current_stock + qty;

      const purchasedAt =
        result?.product?.updatedAt ?? new Date().toISOString();

      const procurementEntry: PurchaseHistory = {
        customer: procurementOfficerDisplay,
        quantityPurchased: qty,
        purchasedAt,
      };

      const prevHistory = product.purchase_history ?? [];
      const updated: InventoryProduct = {
        ...product,
        current_stock: nextStock,
        stock_status: stockStatusFromQuantity(nextStock),
        last_purchase_date: purchasedAt,
        updatedAt: purchasedAt,
        purchase_history: [procurementEntry, ...prevHistory],
      };
      onSuccess(updated, qty);
      onClose();
    } catch {
      /* thunk already toasts */
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-2xl sm:max-w-3xl rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-entry-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-6 sm:p-8 pr-14 sm:pr-16 flex gap-4 sm:gap-5">
          <div className="shrink-0">
            {productImageUrl && !productImageFailed ? (
              <img
                src={productImageUrl}
                alt=""
                onError={() => setProductImageFailed(true)}
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl border border-gray-100 object-cover bg-gray-50"
              />
            ) : (
              <div
                className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400"
                aria-hidden
              >
                <IoImageOutline className="h-10 w-10 sm:h-12 sm:w-12" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="purchase-entry-title"
              className="text-xl font-semibold text-gray-900"
            >
              Purchase Entry For{" "}
              <span className="text-green-700">{product.product_name}</span>
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Log new product purchases to update inventory
            </p>
          </div>
        </div>

        <div className="px-6 sm:px-8 pb-6 sm:pb-8 space-y-5 sm:space-y-6">
          <div className="rounded-xl bg-emerald-50/90 border border-emerald-100/80 px-4 py-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-800">
                Current stock (total on hand)
              </span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStockStatusPillClass(
                  product.stock_status
                )}`}
              >
                {getStockStatusLabel(product.stock_status)}
              </span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {product.current_stock} units
            </p>
            <p className="mt-2 text-xs text-gray-600 leading-relaxed">
              Add Purchase sends your <span className="font-medium">new total</span>{" "}
              to the server (current balance + units below). The number you enter
              is how many units to add, not the new total by itself.
            </p>
          </div>

          <div>
            <label
              htmlFor="purchase-qty"
              className="block text-sm font-semibold text-gray-800 mb-1.5"
            >
              Units to add
            </label>
            <input
              id="purchase-qty"
              type="number"
              min={1}
              step={1}
              value={quantityInput}
              onChange={(e) => setQuantityInput(e.target.value)}
              placeholder="e.g. 30"
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-gray-900 outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600"
            />
          </div>

          <div>
            <span className="block text-sm font-semibold text-gray-800 mb-1.5">
              Procurement Officer
            </span>
            <div
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-gray-900"
              aria-readonly="true"
            >
              <p className="font-medium">{procurementOfficerDisplay}</p>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              Taken from the signed-in admin account and cannot be edited.
            </p>
          </div>

          <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm text-gray-700">
            <p>
              After a successful purchase, related{" "}
              <span className="font-medium text-gray-800">date</span>,{" "}
              <span className="font-medium text-gray-800">time</span>, and{" "}
              <span className="font-medium text-gray-800">status counts</span>{" "}
              will be updated in inventory.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateQuantity.status === "loading"}
              className="flex-1 rounded-lg bg-green-700 py-3 text-center text-sm font-semibold text-white shadow hover:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {updateQuantity.status === "loading" ? "Saving…" : "Add Purchase"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={updateQuantity.status === "loading"}
              className="sm:w-auto rounded-lg border-2 border-red-500 bg-white px-6 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseEntryModal;
